// AppRuntime — renders a sandboxed iframe that compiles + runs the user's
// app. The iframe is loaded same-origin with the API (from
// `${hostname}/apps/<id>/runtime`) so the per-app session cookie and any
// other auth flows just work without CORS gymnastics.
//
// Files are sent to the iframe via postMessage. This preserves live unsaved
// preview — the editor's in-memory file map is forwarded directly. External
// hosts that don't have files in memory can omit them and the runtime
// fetches them via same-origin XHR.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";

interface AppFile {
  content: string;
  type?: string;
}

export type RuntimeEventKind = "error" | "warn" | "uncaught" | "rejection" | "compile";

export interface RuntimeEvent {
  kind: RuntimeEventKind;
  message: string;
  stack?: string | null;
  source?: string | null;
  line?: number | null;
  col?: number | null;
  ts: number;
}

/**
 * "platform" — the editor and internal-app surfaces. Calls the builder API
 *   with the caller's JWT bearer token.
 * "external" — publicly hosted external app at `/x/<appId>`. Calls the
 *   builder routes with same-origin cookies for end-user auth.
 */
export type AppRuntimeMode = "platform" | "external";

export interface AppRuntimeProps {
  /** Optional. When provided, files are postMessage'd into the iframe so
   * unsaved edits preview live. When omitted, the runtime fetches files
   * itself from /apps/<id>/files. */
  files?: Record<string, AppFile | string>;
  /** Required in `mode='platform'`; ignored in `mode='external'`. */
  getToken?: () => Promise<string>;
  /** Required in `mode='platform'`. External apps resolve companyId from
   * the app row on the backend. */
  companyId?: string;
  appId: string;
  hostname: string;
  mode?: AppRuntimeMode;
  /** Bumped to force a rebuild even if file content is identical. */
  reloadKey?: number;
  onRuntimeEvent?: (event: RuntimeEvent) => void;
}

function getContent(file: AppFile | string | undefined): string {
  if (!file) return "";
  return typeof file === "string" ? file : file.content;
}

function flattenFiles(
  files: Record<string, AppFile | string> | undefined,
): Record<string, string> | undefined {
  if (!files) return undefined;
  const out: Record<string, string> = {};
  for (const [path, f] of Object.entries(files)) {
    out[path] = getContent(f);
  }
  return out;
}

export const AppRuntime: React.FC<AppRuntimeProps> = ({
  files,
  getToken,
  companyId,
  appId,
  hostname,
  mode = "platform",
  reloadKey,
  onRuntimeEvent,
}) => {
  const [token, setToken] = useState<string | null>(mode === "external" ? "" : null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);

  const onRuntimeEventRef = useRef(onRuntimeEvent);
  useEffect(() => {
    onRuntimeEventRef.current = onRuntimeEvent;
  }, [onRuntimeEvent]);

  useEffect(() => {
    function handler(ev: MessageEvent) {
      if (!ev.data || typeof ev.data !== "object") return;
      if (iframeRef.current && ev.source !== iframeRef.current.contentWindow) return;
      const data = ev.data as any;
      if (data.__builder === "runtime-ready") {
        setIframeReady(true);
        return;
      }
      if (data.__builder !== "runtime-event") return;
      const cb = onRuntimeEventRef.current;
      if (!cb) return;
      cb({
        kind: data.kind,
        message: data.payload?.message || "",
        stack: data.payload?.stack ?? null,
        source: data.payload?.source ?? null,
        line: data.payload?.line ?? null,
        col: data.payload?.col ?? null,
        ts: data.ts || Date.now(),
      });
    }
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    if (mode === "external") return;
    let cancelled = false;
    (async () => {
      try {
        if (!getToken) throw new Error("getToken is required for mode='platform'");
        const t = await getToken();
        if (!cancelled) setToken(t);
      } catch (e: any) {
        if (!cancelled) setTokenError(String(e?.message || e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, mode]);

  const flatFiles = useMemo(() => flattenFiles(files), [files]);

  const sendCtx = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    if (token === null) return;
    const ctx =
      mode === "external"
        ? { appId, hostname }
        : { token: token || "", companyId, appId, hostname };
    const payload: any = { __builder: "app-ctx", ctx };
    if (flatFiles) payload.files = flatFiles;
    win.postMessage(payload, "*");
  }, [appId, hostname, mode, token, companyId, flatFiles]);

  useEffect(() => {
    if (!iframeReady) return;
    sendCtx();
  }, [iframeReady, sendCtx, reloadKey]);

  if (tokenError) {
    return (
      <Box p={3}>
        <Alert severity="error">{tokenError}</Alert>
      </Box>
    );
  }

  if (token === null) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <CircularProgress size={20} />
        <Typography variant="body2" sx={{ ml: 2 }} color="text.secondary">
          Starting preview…
        </Typography>
      </Box>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      title="App Preview"
      src={`${hostname}/apps/${encodeURIComponent(appId)}/runtime`}
      sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
      allow="clipboard-read; clipboard-write; camera; microphone; geolocation; fullscreen"
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        backgroundColor: "#fff",
      }}
    />
  );
};

export default AppRuntime;
